import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ArrowLeft, Send, Users, ShoppingBag, Wrench, Globe } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const AdminBroadcastPage = () => {
  const navigate = useNavigate();
  const { user, api, loading: authLoading } = useAuth();
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({
    subject: '',
    message: '',
    recipient_type: 'all'
  });

  const recipientOptions = [
    { value: 'all', label: 'Everyone', icon: Globe, description: 'All registered users' },
    { value: 'sellers', label: 'Sellers Only', icon: ShoppingBag, description: 'Users with active seller accounts' },
    { value: 'buyers', label: 'Buyers Only', icon: Users, description: 'Users without seller accounts' },
    { value: 'tradies', label: 'Tradies Only', icon: Wrench, description: 'Users with trade services enabled' }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.subject.trim() || !form.message.trim()) {
      toast.error('Please fill in all fields');
      return;
    }

    setSending(true);
    try {
      const res = await api().post('/admin/broadcast', form);
      toast.success(`Message sent to ${res.data.notifications_created} users!`);
      setForm({ subject: '', message: '', recipient_type: 'all' });
    } catch (err) {
      console.error('Failed to send broadcast', err);
      toast.error(err.response?.data?.detail || 'Failed to send broadcast');
    } finally {
      setSending(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-2 border-teal-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!user || (!user.is_admin && user.email !== 'ebonnie_faith@hotmail.com' && user.email !== 'craftersmarketaus@gmail.com')) {
    navigate('/');
    return null;
  }

  return (
    <div className="min-h-screen bg-stone-50">
      <div className="max-w-2xl mx-auto px-4 py-8">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-stone-500 hover:text-teal-600 mb-6"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>

        <h1 className="text-2xl font-bold text-stone-900 mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
          Broadcast Message
        </h1>
        <p className="text-stone-500 mb-8">
          Send a message to all users or specific groups. Messages will appear as notifications and emails.
        </p>

        <form onSubmit={handleSubmit} className="bg-white border border-stone-200 rounded-lg p-6 space-y-6">
          {/* Recipient Type */}
          <div>
            <Label className="mb-3 block">Send To</Label>
            <div className="grid grid-cols-2 gap-3">
              {recipientOptions.map((option) => {
                const Icon = option.icon;
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setForm({ ...form, recipient_type: option.value })}
                    className={`p-4 border rounded-lg text-left transition-colors ${
                      form.recipient_type === option.value
                        ? 'border-teal-500 bg-teal-50'
                        : 'border-stone-200 hover:border-stone-300'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <Icon className={`h-4 w-4 ${form.recipient_type === option.value ? 'text-teal-600' : 'text-stone-500'}`} />
                      <span className={`font-medium ${form.recipient_type === option.value ? 'text-teal-600' : 'text-stone-900'}`}>
                        {option.label}
                      </span>
                    </div>
                    <p className="text-xs text-stone-500">{option.description}</p>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Subject */}
          <div>
            <Label htmlFor="subject">Subject</Label>
            <Input
              id="subject"
              value={form.subject}
              onChange={(e) => setForm({ ...form, subject: e.target.value })}
              placeholder="e.g., Happy New Year from Crafters Market! 🎉"
              className="mt-1"
            />
          </div>

          {/* Message */}
          <div>
            <Label htmlFor="message">Message</Label>
            <Textarea
              id="message"
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              placeholder="Write your message here..."
              rows={6}
              className="mt-1"
            />
            <p className="text-xs text-stone-500 mt-1">
              This message will be sent as both an in-app notification and an email.
            </p>
          </div>

          {/* Preview */}
          {(form.subject || form.message) && (
            <div className="bg-stone-50 border border-stone-200 rounded-lg p-4">
              <p className="text-xs font-medium text-stone-500 uppercase mb-2">Preview</p>
              <div className="bg-white border border-stone-200 rounded p-3">
                <p className="font-semibold text-stone-900">{form.subject || 'Subject'}</p>
                <p className="text-stone-600 mt-1 whitespace-pre-wrap">{form.message || 'Message content'}</p>
              </div>
            </div>
          )}

          {/* Submit */}
          <Button
            type="submit"
            disabled={sending || !form.subject.trim() || !form.message.trim()}
            className="w-full bg-gradient-to-r from-sky-500 to-teal-500 hover:from-sky-600 hover:to-teal-600 text-white"
          >
            {sending ? (
              <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
            ) : (
              <>
                <Send className="h-4 w-4 mr-2" />
                Send Broadcast
              </>
            )}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default AdminBroadcastPage;
